import { DEFAULT_TIME_ZONE, getZonedDate } from './timezone';

export const MIN_BOOKING_LEAD_MINUTES = 2;

export function pad(value) {
  return String(value).padStart(2, '0');
}

export function isoDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function parseIso(value) {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function addDays(date, diff) {
  const result = new Date(date);
  result.setDate(result.getDate() + diff);
  return result;
}

export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function addMonths(date, diff) {
  return new Date(date.getFullYear(), date.getMonth() + diff, 1);
}

export function timeToMinutes(value) {
  const [hours, minutes] = String(value || '00:00').split(':').map(Number);
  return (Number(hours || 0) * 60) + Number(minutes || 0);
}

export function minutesToTime(total) {
  const normalized = ((total % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${pad(hours)}:${pad(minutes)}`;
}

export function formatLongDate(dateValue) {
  const date = parseIso(dateValue);
  if (!date) return '—';

  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
  }).format(date);
}

export function formatMonthTitle(date) {
  return new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function relativeDayLabel(dateValue, timeZone = DEFAULT_TIME_ZONE) {
  const current = parseIso(dateValue);
  if (!current) return '—';

  const today = startOfDay(getZonedDate(new Date(), timeZone));
  const tomorrow = addDays(today, 1);

  if (isoDate(current) === isoDate(today)) return 'Сегодня';
  if (isoDate(current) === isoDate(tomorrow)) return 'Завтра';

  return new Intl.DateTimeFormat('ru-RU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(current).replace('.', '');
}

export function roundUpToStep(date, stepMins) {
  const step = Math.max(1, Number(stepMins || 30));
  const result = new Date(date);
  result.setSeconds(0, 0);

  const minutes = result.getMinutes();
  const nextMinutes = Math.ceil(minutes / step) * step;
  result.setMinutes(nextMinutes);

  return result;
}

export function buildDateTimeValue(dateValue, timeValue) {
  return `${dateValue}|${timeValue}`;
}

export function getDatePart(value) {
  return String(value || '').split('|')[0] || '';
}

export function getTimePart(value) {
  return String(value || '').split('|')[1] || '';
}

export function buildDateTimeValueByOffset(dateValue, startTime, diffMins) {
  const date = parseIso(dateValue);
  if (!date) return '';

  const start = timeToMinutes(startTime);
  const total = start + Number(diffMins || 0);
  const dayOffset = Math.floor(total / (24 * 60));
  const targetDate = addDays(date, dayOffset);

  return buildDateTimeValue(isoDate(targetDate), minutesToTime(total));
}

export function diffBetweenDateTimeValues(startValue, endValue) {
  const startDate = parseIso(getDatePart(startValue));
  const endDate = parseIso(getDatePart(endValue));
  const startTime = getTimePart(startValue);
  const endTime = getTimePart(endValue);

  if (!startDate || !endDate || !startTime || !endTime) return 0;

  const dayDiff = Math.round((startOfDay(endDate).getTime() - startOfDay(startDate).getTime()) / 86400000);
  return (dayDiff * 24 * 60) + timeToMinutes(endTime) - timeToMinutes(startTime);
}

export function sameMonth(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}
